import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import TriggerModal from "./TriggerModal";
import { Box, Button, Dialog, DialogActions, DialogContent, Grid, Typography } from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { delete_active_icon, delete_disabled_icon, edit_active_icon, edit_disabled_icon, reminder_icon, send_reminder_disable_icon } from "../../../../constants/icons";
import DynamicTable from "../../../../components/Table/DynamicTable";
import { fetchTriggers, removeTrigger, sendReminder } from "../../../../store/triggerSlice";
import './TriggerTable.scss'
import DeleteModal from "../../../../components/DeleteModal/DeleteModal";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ROLE_TYPES } from "../../../../utils/utils";
import { Height } from "@mui/icons-material";
dayjs.extend(utc);
dayjs.extend(timezone);

const tz = "Asia/Kolkata";

const isFutureDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const trigger = new Date(date);
  trigger.setHours(0, 0, 0, 0);

  return trigger > today;
};

const TriggerTable = ({
  open,
  formId,
  bootcampOptions,
  triggerformData,
  handleClose,
  onTriggerActionSuccess,
  onViewResponses
}) => {
  const dispatch = useDispatch();
  const triggersState = useSelector((state) => state.triggers || {});
  const { list: apiData = [], loading = false, totalResults = 0 } = triggersState;
  const security = useSelector((state) => state?.security);
  const roles = security?.roles;
  const loggedInUser = security?.loggedInUser;
  const isSPOC = loggedInUser?.permission?.spoc === true;

  const [triggers, setTriggers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletedID, setDeletedID] = useState(null)
  const isHR = ROLE_TYPES.isHr(roles);


  const handlePageChange = (value) => {
    setCurrentPage(value);
  };
  const tableList = useMemo(() => {
    setTotalPages(Math.ceil(totalResults / 5));
    const firstPageIndex = (currentPage - 1) * 5;
    const lastPageIndex = firstPageIndex + 5;
    return triggers.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, triggers, totalResults]);

  useEffect(() => {
    dispatch(fetchTriggers({ formId }));
  }, [dispatch, formId, bootcampOptions]);


  useEffect(() => {
    if (apiData?.results?.length) {
      const transformed = transformTriggersData(apiData?.results);
      setTriggers(transformed);
    } else {
      setTriggers([]);
    }
  }, [apiData]);
  const transformTriggersData = (apiData) => {
    return apiData.map((item) => ({
      bootcampName: item.bootcamp.name,
      bootcompDetails: item.bootcamp,
      assessmentName: item.assessmentName,
      assessmentDate: item.assessmentDate,
      periodFrom: item.periodFrom,
      periodTo: item.periodTo,
      triggerDate: item.triggerDate,
      responseStats: { "response": item.filledResponses.totalResults, "total": item.totalResponses.totalResults },
      original: item,
      id: item.id,
    }));
  };

  const extractDate = (date) => {
    if (!date) return null;

    return dayjs(date)
      .utc()
      .startOf("day");
  };

  const handleEdit = (row) => {
    const cleanRow = {
      ...row,
      periodFrom: extractDate(row.periodFrom),
      periodTo: extractDate(row.periodTo),
      triggerDate: extractDate(row.triggerDate),
      assessmentDate: extractDate(row.assessmentDate),
      bootcamps: [{ id: row.bootcompDetails.id, name: row.bootcompDetails.name }]
    };
    setEditData(cleanRow);
    setOpenModal(true);
  };


  const handleDelete = () => {
    dispatch(removeTrigger(deletedID)).then(() => {
      handleDeleteModalClose();
      dispatch(fetchTriggers({ formId }));
      onTriggerActionSuccess();
      setCurrentPage(1);
    });
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setDeletedID(null);
  }
  const handleDeleteModalOpen = (row) => {
    setDeletedID(row.id);
    setDeleteModalOpen(true)
  }

  const hanldeSendReminder = (row) => {
    dispatch(sendReminder(row.id)).then(() => {
      onTriggerActionSuccess();
    });
  }


  const handleCreate = () => {
    setEditData(null);
    setOpenModal(true);
  };

  const handleStatsClick = (triggerId) => {
    if (onViewResponses) {
      handleClose();
      onViewResponses(triggerId);
    }
  };

  const columns = useMemo(() => {
    const baseCols = [
      { key: "bootcampName", label: "Bootcamp" },
      {
        key: "triggerDate", label: "Trigger Date", render: (value) => {
          if (!value) return "-";
          const date = new Date(value);
          return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour12: true,
          });
        },
      },
      {
        key: "responseStats",
        label: "Response Stats",
        render: (value, row) => {
          if (!value || typeof value !== "object") return "-";

          const { response = 0, total = 0 } = value;
          const display = `${response}/${total}`;
          return (
            <Box
              onClick={() => handleStatsClick(row.id)}
              sx={{
                color: "#de1186",
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "underline",
                "&:hover": { color: "#bf0f73" }
              }}
            >
              {display}
            </Box>
          );
        },
      },
    ];

    if (triggerformData?.type === "CLASSROOM_ASSESSMENT_PERFORMANCE") {
      return [
        ...baseCols,
        { key: "assessmentName", label: "Assessment Name" },
        {
          key: "assessmentDate", label: "Assessment Date", render: (value) => {
            if (!value) return "-";
            return new Date(value).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              timeZone: "UTC"
            });
          }
        },
      ];
    }

    return [
      ...baseCols,
      {
        key: "periodFrom", label: "Period From", render: (value) => {
          if (!value) return "-";
          return new Date(value).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            timeZone: "UTC"
          });
        },
      },
      {
        key: "periodTo", label: "Period To", render: (value) => {
          if (!value) return "-";
          return new Date(value).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            timeZone: "UTC"
          });
        },
      },
    ];
  }, [triggerformData?.type, onViewResponses]);

  const getPermissions = (row) => {
    const publish = triggerformData?.published;
    const filledCount = row?.responseStats?.response || 0;
    const totalCount = row?.responseStats?.total || 0;
    const hasResponses = totalCount >= 1;
    const isFullyResponded = filledCount > 0 && filledCount === totalCount;
    const triggerDate = row?.triggerDate;
    const isTriggeredNowOrPast = !isFutureDate(triggerDate);
    const canManage = isHR || isSPOC;
    return {
      edit: !canManage || hasResponses,
      delete: !canManage || hasResponses,
      reminder:
        !isHR ||
        !isTriggeredNowOrPast ||
        !publish ||
        isFullyResponded,
    };
  };


  const actions = [
    {
      icon: edit_active_icon,
      tooltip: "Edit",
      disabledImage: edit_disabled_icon,
      disabledTooltip: "You don't have permission to edit",
      disabled: (row) => getPermissions(row).edit, onClick: handleEdit,
      hideForSPOC: false,
    },
    {
      icon: delete_active_icon, tooltip: "Delete",
      onClick: handleDeleteModalOpen,
      disabled: (row) => getPermissions(row).delete,
      hideForSPOC: false,
    },
    {
      icon: reminder_icon,
      disabledImage: send_reminder_disable_icon,
      tooltip: "Send Reminder",
      disabledTooltip: "You can't send reminder",
      onClick: (row) => hanldeSendReminder(row),
      disabled: (row) => getPermissions(row).reminder,
      hideForSPOC: true,
    },

  ].filter(action => !(isSPOC && action.hideForSPOC));



  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" className="trigger-table-modal">
        <DialogContent sx={{ paddingBottom: 0 }}>
          <Grid item className="header-container" md={12}>
            <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5, opacity: 0.9 }}>
              <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#de1186' }} />
              <Typography variant="font_12" sx={{ color: "#4b4b4b", fontStyle: "italic" }}>
                Responses will be triggerd and/or users will be notified daily at 10:00 AM
              </Typography>
            </Box>
            {(
              <Box className="header-right-icons new-trigger-button">
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ height: "1.875rem" }}
                  onClick={handleCreate}
                  disabled={!(isSPOC || isHR)}
                >
                  <Typography color="secondary" variant="outlineBtnLabel">
                    New Trigger
                  </Typography>
                </Button>
              </Box>
            )}
          </Grid>

          <Box sx={{
            maxHeight: "297px",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}>
            <DynamicTable
              title="Form Triggers"
              columns={columns}
              rows={tableList}
              actions={actions}
              isLoading={loading}
              totalResult={totalResults}
              totalPages={totalPages}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
            />
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined"
            color="secondary">
            <Typography variant="outlineBtnLabel">Close</Typography>
          </Button>
        </DialogActions>
      </Dialog >

      {deleteModalOpen && (
        <DeleteModal
          open={deleteModalOpen}
          handleClose={handleDeleteModalClose}
          handleDelete={handleDelete}
        />)}

      {openModal && (
        <TriggerModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          initialData={editData}
          bootcampOptions={triggerformData?.associatedBootcamps}
          formId={triggerformData?.id}
          formType={triggerformData?.type}
          formCreatedDate={triggerformData?.dateCreated}
          onTriggerActionSuccess={onTriggerActionSuccess}
        />
      )
      }
    </>
  );
};

export default TriggerTable;
